import React, { useState, useEffect, useCallback } from 'react'
import {
  Button,
  TextField,
  Heading,
  Text,
  Toast,
  Box,
  Flex,
  Badge,
} from '@vibe/core'
import { Check, Warning, Settings, Link as LinkIcon } from '@vibe/icons'
import { apiService } from '../../services/api'
import { getErrorMessage } from '../../utils/errorHandling'
import type {
  JobIntegration,
  CreateJobIntegrationRequest,
  UpdateJobIntegrationRequest,
} from '../../types'

interface JobIntegrationSectionProps {
  solicitudId: string
  onIntegrationChange?: (integration: JobIntegration | null) => void
}

interface FormErrors {
  [key: string]: string
}

export const JobIntegrationSection: React.FC<JobIntegrationSectionProps> = ({
  solicitudId,
  onIntegrationChange,
}) => {
  // State management
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{
    message: string
    type: 'positive' | 'negative'
  } | null>(null)

  // Integration data
  const [integration, setIntegration] = useState<JobIntegration | null>(null)
  const [isEditing, setIsEditing] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    spreadsheet_url: '',
    sync_status: 'active' as 'active' | 'paused' | 'error',
  })
  const [errors, setErrors] = useState<FormErrors>({})

  // Load integration data
  const loadIntegration = useCallback(async () => {
    if (!solicitudId) return

    try {
      setIsLoading(true)
      setError(null)

      const response = await apiService.getJobIntegration(solicitudId)
      setIntegration(response.data)
      setFormData({
        spreadsheet_url: response.data.spreadsheet_url,
        sync_status: response.data.sync_status,
      })
      onIntegrationChange?.(response.data)
    } catch (err) {
      // 404 is expected if no integration exists yet
      if ((err as any)?.status !== 404) {
        console.error('Error loading job integration:', err)
        setError(
          getErrorMessage(err) ||
            'Error al cargar la configuración de integración'
        )
      }
      setIntegration(null)
      onIntegrationChange?.(null)
    } finally {
      setIsLoading(false)
    }
  }, [solicitudId, onIntegrationChange])

  // Initial load
  useEffect(() => {
    loadIntegration()
  }, [loadIntegration])

  // Form validation
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.spreadsheet_url.trim()) {
      newErrors.spreadsheet_url = 'La URL de Google Sheets es obligatoria'
    } else {
      const validation = apiService.validateGoogleSheetsUrl(
        formData.spreadsheet_url
      )
      if (!validation.isValid) {
        newErrors.spreadsheet_url = validation.errors[0]
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData])

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      setToast({
        message: 'Por favor corrige los errores antes de continuar',
        type: 'negative',
      })
      return
    }

    setIsSubmitting(true)

    try {
      if (integration) {
        // Update existing integration
        const updateData: UpdateJobIntegrationRequest = {
          spreadsheet_url: formData.spreadsheet_url,
          sync_status: formData.sync_status,
        }

        const response = await apiService.updateJobIntegration(
          solicitudId,
          updateData
        )
        setIntegration(response.data)
        onIntegrationChange?.(response.data)
        setToast({
          message: 'Integración actualizada exitosamente',
          type: 'positive',
        })
      } else {
        // Create new integration
        const createData: CreateJobIntegrationRequest = {
          integration_type: 'google_forms',
          spreadsheet_url: formData.spreadsheet_url,
        }

        const response = await apiService.createJobIntegration(
          solicitudId,
          createData
        )
        setIntegration(response.data)
        onIntegrationChange?.(response.data)
        setToast({
          message: 'Integración creada exitosamente',
          type: 'positive',
        })
      }

      setIsEditing(false)
    } catch (error) {
      console.error('Error saving job integration:', error)
      setToast({
        message:
          error instanceof Error
            ? error.message
            : 'Error al guardar la integración',
        type: 'negative',
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [formData, integration, solicitudId, validateForm, onIntegrationChange])

  // Handle cancel
  const handleCancel = useCallback(() => {
    setIsEditing(false)
    setFormData({
      spreadsheet_url: integration?.spreadsheet_url || '',
      sync_status: integration?.sync_status || 'active',
    })
    setErrors({})
  }, [integration])

  // Handle delete
  const handleDelete = useCallback(async () => {
    if (!integration) return

    const confirmed = window.confirm(
      '¿Estás seguro de que quieres eliminar esta integración? Esta acción no se puede deshacer.'
    )
    if (!confirmed) return

    setIsSubmitting(true)

    try {
      await apiService.deleteJobIntegration(solicitudId)
      setIntegration(null)
      onIntegrationChange?.(null)
      setToast({
        message: 'Integración eliminada exitosamente',
        type: 'positive',
      })
    } catch (error) {
      console.error('Error deleting job integration:', error)
      setToast({
        message:
          error instanceof Error
            ? error.message
            : 'Error al eliminar la integración',
        type: 'negative',
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [integration, solicitudId, onIntegrationChange])

  // Auto-hide toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [toast])

  if (isLoading) {
    return (
      <Box className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </div>
      </Box>
    )
  }

  return (
    <Box className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6">
        {/* Toast notifications */}
        {toast && (
          <Toast
            open={true}
            onClose={() => setToast(null)}
            className="fixed top-4 right-4 z-50"
            type={toast.type}
          >
            {toast.message}
          </Toast>
        )}

        <Flex justify="space-between" align="center" className="mb-6">
          <div>
            <Heading type="h3" className="mb-2">
              Integración con Google Forms
            </Heading>
            <Text color="secondary">
              Sincroniza automáticamente CVs desde Google Forms responses
            </Text>
          </div>
          <div className="flex items-center space-x-2">
            {integration && (
              <Badge
                color={
                  integration.sync_status === 'active'
                    ? 'primary'
                    : integration.sync_status === 'paused'
                      ? 'notification'
                      : 'negative'
                }
              >
                {integration.sync_status === 'active'
                  ? 'Activo'
                  : integration.sync_status === 'paused'
                    ? 'Pausado'
                    : 'Error'}
              </Badge>
            )}
            <LinkIcon className="text-secondary-500" />
          </div>
        </Flex>

        {/* Error state */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <Warning className="text-red-600 mr-2" />
              <Text className="text-red-800">{error}</Text>
            </div>
          </div>
        )}

        {!isEditing ? (
          // View mode
          <div className="space-y-4">
            {integration ? (
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Check className="text-green-600" />
                  <Text>Integración configurada</Text>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3">
                    <div>
                      <Text className="text-sm text-secondary-600 mb-1">
                        URL de Google Sheets
                      </Text>
                      <Text className="font-mono text-sm break-all">
                        {integration.spreadsheet_url}
                      </Text>
                    </div>

                    {integration.cv_column_index !== undefined && (
                      <div>
                        <Text className="text-sm text-secondary-600 mb-1">
                          Columna de CVs detectada
                        </Text>
                        <Text>Columna {integration.cv_column_index + 1}</Text>
                      </div>
                    )}

                    {integration.last_sync_timestamp && (
                      <div>
                        <Text className="text-sm text-secondary-600 mb-1">
                          Última sincronización
                        </Text>
                        <Text>
                          {new Date(
                            integration.last_sync_timestamp
                          ).toLocaleString('es-ES')}
                        </Text>
                      </div>
                    )}

                    {integration.error_details && (
                      <div>
                        <Text className="text-sm text-secondary-600 mb-1">
                          Detalles del error
                        </Text>
                        <Text className="text-red-600 text-sm">
                          {integration.error_details}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>

                <Flex gap="small">
                  <Button
                    onClick={() => setIsEditing(true)}
                    disabled={isSubmitting}
                  >
                    Editar Integración
                  </Button>
                  <Button
                    kind="tertiary"
                    onClick={loadIntegration}
                    disabled={isSubmitting}
                  >
                    Actualizar
                  </Button>
                  <Button
                    kind="tertiary"
                    onClick={handleDelete}
                    disabled={isSubmitting}
                    className="text-red-600 hover:text-red-700"
                  >
                    Eliminar
                  </Button>
                </Flex>
              </div>
            ) : (
              <div className="text-center py-8">
                <Heading type="h3" className="mb-2">
                  Sin integración configurada
                </Heading>
                <Text color="secondary" className="mb-4">
                  Configura una integración con Google Forms para sincronizar
                  CVs automáticamente
                </Text>
                <Button
                  onClick={() => setIsEditing(true)}
                  disabled={isSubmitting}
                >
                  Configurar Integración
                </Button>
              </div>
            )}
          </div>
        ) : (
          // Edit mode
          <div className="space-y-6">
            <div>
              <TextField
                title="URL de Google Sheets *"
                placeholder="https://docs.google.com/spreadsheets/d/..."
                value={formData.spreadsheet_url}
                onChange={value =>
                  setFormData(prev => ({
                    ...prev,
                    spreadsheet_url: value,
                  }))
                }
                required
              />
              {errors.spreadsheet_url && (
                <Text className="text-red-600 text-sm mt-1">
                  {errors.spreadsheet_url}
                </Text>
              )}
              <Text className="text-sm text-secondary-600 mt-2">
                La URL de la hoja de cálculo de Google que contiene las
                respuestas del formulario
              </Text>
            </div>

            {integration && (
              <div>
                <Text className="mb-2 font-medium">
                  Estado de sincronización
                </Text>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="sync_status"
                      value="active"
                      checked={formData.sync_status === 'active'}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          sync_status: e.target.value as
                            | 'active'
                            | 'paused'
                            | 'error',
                        }))
                      }
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <Text>Activo - Sincronización automática habilitada</Text>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="sync_status"
                      value="paused"
                      checked={formData.sync_status === 'paused'}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          sync_status: e.target.value as
                            | 'active'
                            | 'paused'
                            | 'error',
                        }))
                      }
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <Text>
                      Pausado - Sincronización temporalmente deshabilitada
                    </Text>
                  </label>
                </div>
              </div>
            )}

            <Flex gap="small">
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {integration ? 'Actualizar' : 'Crear'} Integración
              </Button>
              <Button
                kind="tertiary"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
            </Flex>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <Heading type="h3" className="mb-3">
            Instrucciones de configuración
          </Heading>
          <div className="space-y-2">
            <Text className="text-sm text-secondary-600">
              1. Crea un Google Form y configúralo para guardar respuestas en
              Google Sheets
            </Text>
            <Text className="text-sm text-secondary-600">
              2. Asegúrate de que una de las preguntas permita subir archivos
              (CVs) a Google Drive
            </Text>
            <Text className="text-sm text-secondary-600">
              3. Comparte la hoja de cálculo con la cuenta de servicio
              configurada en integraciones
            </Text>
            <Text className="text-sm text-secondary-600">
              4. Copia la URL de la hoja de cálculo y pégala en el campo de
              arriba
            </Text>
          </div>
        </div>
      </div>
    </Box>
  )
}

export default JobIntegrationSection
